import React from "react";

/**
 * Reusable state primitives for empty / error / loading screens.
 * Use these instead of inlining ad-hoc divs so every page feels consistent
 * and accessible (proper roles + aria-live announcements).
 */

type BaseProps = {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
};

const wrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "48px 20px",
  gap: 8,
  color: "var(--tx2)",
};

export function EmptyState({ title = "Nada por aquí todavía", description, icon = "📭", action, className }: BaseProps) {
  return (
    <div role="status" aria-live="polite" className={className} style={wrapStyle}>
      <div aria-hidden="true" style={{ fontSize: 36, marginBottom: 4 }}>{icon}</div>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--tx)", margin: 0 }}>{title}</h3>
      {description && <p style={{ fontSize: 12.5, color: "var(--tx3)", maxWidth: 360, margin: 0 }}>{description}</p>}
      {action && <div style={{ marginTop: 10 }}>{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = "Algo salió mal",
  description = "No pudimos cargar esta sección. Intenta de nuevo.",
  icon = "⚠️",
  action,
  className,
  onRetry,
}: BaseProps & { onRetry?: () => void }) {
  return (
    <div role="alert" aria-live="assertive" className={className} style={wrapStyle}>
      <div aria-hidden="true" style={{ fontSize: 36, marginBottom: 4 }}>{icon}</div>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--tx)", margin: 0 }}>{title}</h3>
      <p style={{ fontSize: 12.5, color: "var(--tx3)", maxWidth: 360, margin: 0 }}>{description}</p>
      {(action || onRetry) && (
        <div style={{ marginTop: 10 }}>
          {action ?? (
            <button className="btn bp" onClick={onRetry} type="button">
              Reintentar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function LoadingState({ label = "Cargando…", className }: { label?: string; className?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={className}
      style={{ ...wrapStyle, gap: 12 }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 32,
          height: 32,
          border: "3px solid var(--bd2)",
          borderTopColor: "var(--ac)",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <span style={{ fontSize: 12.5, color: "var(--tx3)" }}>{label}</span>
      <span className="sr-only">{label}</span>
    </div>
  );
}